import React, { useState } from 'react';
import AppSelect from '@/components/atoms/AppSelect';
import useAppModal from '@/hooks/useAppModal';
import type { VisitClinicMassager } from '@/types/visitClinic.types';

interface Props {
  massagers: VisitClinicMassager[];
  /** Current main massager's id (massager_id) */
  value: number | null;
  /** Current main massager's clinic_location_id (for composite key matching) */
  clinicLocationId?: number | null;
  /** Called with both ids when selection changes (or nulls when cleared) */
  onChange: (massagerId: number | null, clinicLocationId: number | null) => void;
  onClearPendingData?: () => void;
  hasUnsavedData?: boolean;
  disabled?: boolean;
  isChangeBlocked?: boolean;
  blockedMessage?: string;
  /** A2: empty option label — shown when no massager selected */
  placeholder?: string;
}

/**
 * 主な施術担当者 dropdown with confirmation dialog when changing the selection.
 * BD §5.1.11: switching with unsaved data → discard warning + clear pending
 * BD §5.1.12: switching to another massager → simple change confirm
 *
 * Behavior:
 * - First selection (value was null, no unsaved data): onChange immediately — no「他の施術師に変更」confirm (A2).
 * - When user confirms a change while hasUnsavedData=true: calls onClearPendingData() then onChange()
 * - When user confirms a normal massager switch: calls only onChange()
 * - When user cancels: nothing happens (controlled component reverts automatically)
 */
const VisitClinicMassagerSelect: React.FC<Props> = ({
  massagers,
  value,
  clinicLocationId,
  onChange,
  onClearPendingData,
  hasUnsavedData,
  disabled,
  isChangeBlocked,
  blockedMessage = 'ロックされているため、変更できません。',
  placeholder = '主な施術担当者',
}) => {
  const { confirm, error } = useAppModal();
  const [resyncKey, setResyncKey] = useState(0);
  // BD §5.1: the main massager is pinned to the head of the list; everyone else keeps
  // the 施術師番号 order the API sends. A2 production does the same (pull + prepend in
  // VisitClinicController::editInit) — one practitioner can belong to several 拠点, so
  // the pinned entry is matched on the massager/location pair, not on massager alone.
  const orderedMassagers =
    value == null
      ? massagers
      : [
          ...massagers.filter(
            m => m.massager_id === value && m.clinic_location_id === clinicLocationId
          ),
          ...massagers.filter(
            m => !(m.massager_id === value && m.clinic_location_id === clinicLocationId)
          ),
        ];

  const options: { value: string | number; label: string }[] = orderedMassagers.map(m => ({
    value: m.id,
    label: m.label,
  }));

  if (value == null) {
    options.unshift({ value: '', label: placeholder });
  }

  // Resolve composite id for the currently selected entry (fallback: first by massager_id).
  const selectedEntryId = (() => {
    if (value == null) return '';
    const exact = massagers.find(
      m => m.massager_id === value && m.clinic_location_id === clinicLocationId
    );
    if (exact) return exact.id;
    const byMassager = massagers.find(m => m.massager_id === value);
    return byMassager?.id;
  })();

  const handleChange = (newVal: unknown) => {
    const entryId = (newVal as string | null) ?? null;
    const entry = entryId ? (massagers.find(m => m.id === entryId) ?? null) : null;
    const nextMassagerId = entry?.massager_id ?? null;
    const nextLocationId = entry?.clinic_location_id ?? null;
    if (nextMassagerId === value && nextLocationId === clinicLocationId) return;

    if (isChangeBlocked) {
      error({
        title: blockedMessage,
        okText: 'OK',
      });
      setResyncKey(k => k + 1);
      return;
    }

    // BD §5.1.11: if changing while data exists → discard warning
    // BD §5.1.12: if normal switch → simple confirm
    const shouldWarnAboutDataLoss = hasUnsavedData;
    // A2 culum_control: when massagerId === '' on first selection -> do not confirm「他の施術師に変更」— show the schedule immediately.
    const isFirstSelectionFromEmpty =
      value == null && nextMassagerId !== null && !shouldWarnAboutDataLoss;

    if (isFirstSelectionFromEmpty) {
      onChange(nextMassagerId, nextLocationId);
      return;
    }

    const confirmTitle = shouldWarnAboutDataLoss ? '施術予定の破棄' : '施術師の変更';
    const confirmDescription = shouldWarnAboutDataLoss
      ? [
          '現在入力中の施術予定は破棄されます。',
          '保存する場合はキャンセル後に登録ボタンを押してください。',
        ]
      : '他の施術師に変更しますか？';

    confirm({
      title: confirmTitle,
      content: (
        <div>
          {Array.isArray(confirmDescription) ? (
            confirmDescription.map((line, i) => <p key={i}>{line}</p>)
          ) : (
            <p>{confirmDescription}</p>
          )}
        </div>
      ),
      okButtonProps: { danger: shouldWarnAboutDataLoss ? true : undefined },
      onOk: () => {
        if (shouldWarnAboutDataLoss && onClearPendingData) {
          onClearPendingData();
        }
        onChange(nextMassagerId, nextLocationId);
      },
      onCancel: () => {
        // Force AppSelect to remount so its internal state re-syncs to the old controlled value.
        setResyncKey(k => k + 1);
      },
      okText: 'OK',
      cancelText: 'キャンセル',
    });
  };

  return (
    <AppSelect
      key={resyncKey}
      options={options}
      value={selectedEntryId}
      onChange={handleChange}
      className="w-[45%]"
      disabled={disabled}
    />
  );
};

export default VisitClinicMassagerSelect;
